import { Loader2, Rocket, X } from "lucide-react";
import { useEffect, useState } from "react";
import { inputClass, labelClass, textareaClass } from "@/components/admin/companyProfile/styles";
import { useToast } from "@/components/shared";
import { microAppService } from "@/lib/services";
import type { MicroApp, MicroAppBindingsPreview, MicroAppDataMode } from "@/types";
import { DataSourceModeChooser } from "./DataSourceModeChooser";

interface PublishAppDialogProps {
  attachmentUuid: string;
  defaultName: string;
  onClose: () => void;
  onPublished: (app: MicroApp) => void;
}

/**
 * Publish dialog — name + description, plus a per-source data-mode chooser when
 * the dashboard binds live data. Lets the author decide *whose access* serves
 * each source (and whether it's live or frozen) before the app is shared.
 * See docs/MICRO_APPS_DATAPROVIDER_GENERALIZATION.md §13.
 */
export function PublishAppDialog({
  attachmentUuid,
  defaultName,
  onClose,
  onPublished,
}: Readonly<PublishAppDialogProps>) {
  const { showToast } = useToast();
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState("");
  const [preview, setPreview] = useState<MicroAppBindingsPreview | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [modes, setModes] = useState<Record<string, MicroAppDataMode>>({});
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await microAppService.getAttachmentBindings(attachmentUuid);
        if (cancelled) return;
        setPreview(data);
        // Seed the chooser with the generator's current per-source mode.
        const seed: Record<string, MicroAppDataMode> = {};
        for (const s of data.sources) seed[s.provider] = (s.currentMode as MicroAppDataMode) ?? "caller";
        setModes(seed);
      } catch {
        // Distinguish "preview fetch failed" from "static dashboard" so the
        // author doesn't unknowingly publish without setting data modes.
        if (!cancelled) {
          setPreview({ isLive: false, sources: [] });
          setPreviewFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachmentUuid]);

  const sources = preview?.sources ?? [];
  const anyShared = sources.some((s) => modes[s.provider] === "author");

  async function handlePublish() {
    setPublishing(true);
    try {
      // Flatten the per-source choice into per-binding overrides.
      const authModeOverrides: Record<string, string> = {};
      for (const s of sources) {
        const chosen = modes[s.provider];
        if (chosen && chosen !== s.currentMode) {
          for (const key of s.bindingKeys) authModeOverrides[key] = chosen;
        }
      }
      const { data } = await microAppService.publish({
        attachmentUuid,
        name: name.trim() || defaultName,
        description: description.trim() || null,
        authModeOverrides: Object.keys(authModeOverrides).length ? authModeOverrides : null,
      });
      showToast(`Published “${data.name}”`, "success");
      onPublished(data);
    } catch {
      showToast("Failed to publish micro app", "error");
      setPublishing(false);
    }
  }

  return (
    <dialog // NOSONAR — native <dialog> requires showModal()/close() lifecycle; using open attribute with custom overlay instead
      open
      className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50 p-4 m-0 max-w-none max-h-none w-full h-full border-none"
      aria-modal="true"
      aria-label="Publish as app"
    >
      <div className="bg-surface-primary border border-border-primary rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-semibold text-content-primary">Publish as app</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-interactive-active rounded-lg transition"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-content-tertiary" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          <div>
            <label htmlFor="publish-name" className={labelClass}>
              Name
            </label>
            <input
              id="publish-name"
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dashboard name"
              maxLength={255}
            />
          </div>
          <div>
            <label htmlFor="publish-desc" className={labelClass}>
              Description <span className="text-content-tertiary font-normal">(optional)</span>
            </label>
            <textarea
              id="publish-desc"
              className={textareaClass}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this app for?"
              maxLength={2000}
            />
          </div>

          <DataSourceModeChooser
            sources={sources}
            modes={modes}
            loading={loading}
            onModeChange={(provider, mode) => setModes((prev) => ({ ...prev, [provider]: mode }))}
          />

          {previewFailed && (
            <p className="text-xs rounded-md bg-status-warning/10 text-status-warning p-2.5">
              Couldn't check this dashboard's data sources. If it uses live data, the default sharing mode ("your own
              access") will apply — you can change modes later by republishing.
            </p>
          )}

          {anyShared && (
            <p className="text-xs rounded-md bg-brand-primary/10 text-content-secondary p-2.5">
              You're sharing live data under <span className="font-medium text-content-primary">your access</span> —
              viewers won't need their own. You can revoke this later from the app's settings.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-border-primary">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg text-content-secondary hover:bg-interactive-hover transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePublish}
            disabled={publishing || loading || !name.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-brand-primary text-white hover:opacity-90 transition disabled:opacity-50"
          >
            {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            {publishing ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>
    </dialog>
  );
}
